import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Search, ShieldCheck, Smartphone, Battery, RefreshCcw, Ban } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-[#666] focus:border-white/40 focus:outline-none transition-colors duration-300";

export default function ProtectionMine() {
  const [lookup, setLookup] = useState({ contract_number: "", email: "" });
  const [contract, setContract] = useState(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [pricing, setPricing] = useState([]);
  const [transfer, setTransfer] = useState({ new_device: "", new_device_serial: "", note: "" });
  const [transferSubmitting, setTransferSubmitting] = useState(false);
  const [transferSent, setTransferSent] = useState(false);

  useEffect(() => { api.get("/pricing").then((r) => setPricing(r.data)).catch(() => {}); }, []);

  const doLookup = async (e) => {
    e.preventDefault();
    setChecking(true); setError(""); setContract(null); setTransferSent(false); setShowTransfer(false);
    try {
      const { data } = await api.post("/protection/lookup", lookup);
      setContract(data);
      if (pricing[0]) setTransfer((p) => ({ ...p, new_device: pricing[0].model }));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Vertrag nicht gefunden.");
    } finally { setChecking(false); }
  };

  const cancelContract = async () => {
    if (!window.confirm("Möchtest du dein Schutzpaket wirklich fristgerecht kündigen?")) return;
    setCancelling(true);
    try {
      const { data } = await api.post("/protection/cancel", lookup);
      setContract((c) => ({ ...c, cancel_effective_date: data.cancel_effective_date }));
      toast.success(`Kündigung bestätigt. Vertragsende: ${new Date(data.cancel_effective_date).toLocaleDateString("de-DE")}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Kündigung fehlgeschlagen.");
    } finally { setCancelling(false); }
  };

  const submitTransfer = async (e) => {
    e.preventDefault();
    if (!transfer.new_device_serial.trim()) { toast.error("Bitte Seriennummer des neuen Geräts angeben."); return; }
    setTransferSubmitting(true);
    try {
      await api.post("/protection/transfer-request", { ...lookup, ...transfer });
      setTransferSent(true);
      toast.success("Umschreibung angefragt — wir prüfen deine Anfrage.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Anfrage fehlgeschlagen.");
    } finally { setTransferSubmitting(false); }
  };

  return (
    <section data-testid="protection-mine" className="relative z-10 bg-[#0a0a0a] py-24 lg:py-32 border-t border-white/10 min-h-[70vh]">
      <div className="max-w-2xl mx-auto px-6 lg:px-10">
        <ShieldCheck size={28} className="text-white mb-5" />
        <h1 className="font-display text-white text-3xl sm:text-4xl lg:text-5xl leading-[0.95] mb-10">
          Mein Schutzpaket
        </h1>

        {!contract && (
          <form onSubmit={doLookup} data-testid="protection-mine-lookup-form" className="space-y-4">
            <input data-testid="protection-mine-contract-number" value={lookup.contract_number}
              onChange={(e) => setLookup((p) => ({ ...p, contract_number: e.target.value }))}
              placeholder="Vertragsnummer, z. B. SP-000001" className={inputCls} />
            <input data-testid="protection-mine-email" type="email" value={lookup.email}
              onChange={(e) => setLookup((p) => ({ ...p, email: e.target.value }))}
              placeholder="E-Mail" className={inputCls} />
            {error && <p data-testid="protection-mine-lookup-error" className="text-red-400 text-sm">{error}</p>}
            <button type="submit" disabled={checking} data-testid="protection-mine-lookup-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#050505] py-4 font-medium hover:bg-[#d1d5db] transition-colors disabled:opacity-50">
              <Search size={16} /> {checking ? "Suche…" : "Vertrag anzeigen"}
            </button>
          </form>
        )}

        {contract && (
          <div data-testid="protection-mine-details" className="space-y-6">
            <div className="rounded-2xl border border-white/10 bg-[#050505] p-6">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="font-mono text-sm text-white">{contract.contract_number}</p>
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${contract.status === "aktiv" ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" : "bg-red-500/20 text-red-300 border border-red-500/30"}`}>
                  {contract.status === "aktiv" ? "Aktiv" : contract.status === "beendet" ? "Beendet" : "Ausstehend"}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-5 text-sm">
                <div><p className="text-[#888]">Gerät</p><p className="text-white mt-1">{contract.device}</p></div>
                <div><p className="text-[#888]">Seriennummer</p><p className="text-white mt-1 font-mono text-xs">{contract.device_serial}</p></div>
                <div><p className="text-[#888]">Monatsbeitrag</p><p className="text-white mt-1 font-mono">{contract.monthly_price.toFixed(2)} €</p></div>
                <div><p className="text-[#888]">Mindestlaufzeit bis</p><p className="text-white mt-1">{contract.min_term_end_date ? new Date(contract.min_term_end_date).toLocaleDateString("de-DE") : "–"}</p></div>
                <div>
                  <p className="text-[#888]">{contract.cancel_effective_date ? "Vertragsende" : "Nächste Abbuchung"}</p>
                  <p className="text-white mt-1">
                    {contract.cancel_effective_date
                      ? new Date(contract.cancel_effective_date).toLocaleDateString("de-DE")
                      : contract.next_billing_date ? new Date(contract.next_billing_date).toLocaleDateString("de-DE") : "–"}
                  </p>
                </div>
                <div><p className="text-[#888]">Kontingent dieses Jahr</p><p className="text-white mt-1">{contract.repairs_remaining_this_year} von {contract.repairs_per_year} offen</p></div>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-[#050505] p-6">
              <p className="text-sm text-white mb-3">Reparatur-Historie</p>
              {contract.repair_history.length === 0 ? (
                <p className="text-sm text-[#666]">Noch keine Reparatur über dieses Schutzpaket genutzt.</p>
              ) : (
                <ul className="space-y-2">
                  {contract.repair_history.map((h, i) => (
                    <li key={i} data-testid={`protection-mine-history-${i}`} className="flex items-center gap-2 text-sm text-[#ccc]">
                      {h.repair_type === "display" ? <Smartphone size={14} className="text-[#888]" /> : <Battery size={14} className="text-[#888]" />}
                      {h.repair_type === "display" ? "Display-Reparatur" : "Akkutausch"} — {new Date(h.date).toLocaleDateString("de-DE")}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {contract.status === "aktiv" && (
              <div className="flex flex-col sm:flex-row gap-3">
                {!contract.cancel_effective_date && (
                  <button onClick={cancelContract} disabled={cancelling} data-testid="protection-mine-cancel-btn"
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-red-500/30 text-red-300 py-3.5 text-sm font-medium hover:bg-red-500/10 transition-colors disabled:opacity-50">
                    <Ban size={16} /> {cancelling ? "Wird gekündigt…" : "Fristgerecht kündigen"}
                  </button>
                )}
                <button onClick={() => setShowTransfer((s) => !s)} data-testid="protection-mine-transfer-btn"
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-white/20 text-white py-3.5 text-sm font-medium hover:bg-white/5 transition-colors">
                  <RefreshCcw size={16} /> Umschreibung beauftragen
                </button>
              </div>
            )}

            {contract.cancel_effective_date && (
              <p data-testid="protection-mine-cancel-notice" className="text-sm text-amber-300">
                Dein Vertrag wurde gekündigt und endet zum {new Date(contract.cancel_effective_date).toLocaleDateString("de-DE")}.
              </p>
            )}

            {showTransfer && !transferSent && (
              <form onSubmit={submitTransfer} data-testid="protection-mine-transfer-form" className="rounded-2xl border border-white/10 bg-[#050505] p-6 space-y-4">
                <p className="text-sm text-white">Neues Gerät übertragen</p>
                <select data-testid="protection-mine-transfer-device" value={transfer.new_device}
                  onChange={(e) => setTransfer((p) => ({ ...p, new_device: e.target.value }))} className={inputCls}>
                  {pricing.map((p) => <option key={p.id} value={p.model} className="bg-[#0a0a0a]">{p.model}</option>)}
                </select>
                <input data-testid="protection-mine-transfer-serial" value={transfer.new_device_serial}
                  onChange={(e) => setTransfer((p) => ({ ...p, new_device_serial: e.target.value }))}
                  placeholder="Seriennummer / IMEI des neuen Geräts" className={inputCls} />
                <textarea data-testid="protection-mine-transfer-note" value={transfer.note} rows={3}
                  onChange={(e) => setTransfer((p) => ({ ...p, note: e.target.value }))}
                  placeholder="Nachricht (optional)" className={inputCls} />
                <button type="submit" disabled={transferSubmitting} data-testid="protection-mine-transfer-submit"
                  className="w-full inline-flex items-center justify-center rounded-full bg-white text-[#050505] py-3.5 text-sm font-medium hover:bg-[#d1d5db] transition-colors disabled:opacity-50">
                  {transferSubmitting ? "Wird gesendet…" : "Anfrage senden"}
                </button>
              </form>
            )}

            {transferSent && (
              <p data-testid="protection-mine-transfer-sent" className="text-sm text-emerald-300">
                Deine Umschreibungsanfrage ist eingegangen und wird von uns geprüft. Du erhältst eine E-Mail, sobald sie genehmigt wurde.
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
